import React, { useEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';

import CheckboxInput from 'CommonComponents/CheckboxInput';
import SelectionChip from 'CommonComponents/SelectionChip';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';

const sameValue = (a, b) => String(a) === String(b);

// The field's chips are the compact squared tags of a form field, not the
// rounded pills TreeMultiSelect lists under its trigger.
const FIELD_CHIP = '!tw-rounded-md !tw-px-2 !tw-py-0.5 !tw-text-xs !tw-font-normal';
const FOOTER_LINK = 'tw-text-xs tw-font-medium tw-text-blue hover:tw-underline disabled:tw-text-charcoal-50 disabled:hover:tw-no-underline';

/**
 * Flat, controlled multi-select whose list stays open while items are ticked.
 * The field holds the chosen items as removable chips and a search box that
 * filters the list. The list closes on Done, a click outside it or Escape;
 * Select all ticks every item the search shows.
 *
 * `options` are `{ value, label }`; `value` is the array of chosen values.
 * `id` lands on the search box, so a `<label htmlFor>` focuses it.
 */
export default function CheckboxMultiSelect({ id, options = [], value = [], onChange, placeholder, searchPlaceholder, disabled = false, invalid = false }) {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);
    const listId = id ? `${id}-list` : undefined;

    const selected = useMemo(() => new Set(value.map(String)), [value]);
    const isSelected = (optionValue) => selected.has(String(optionValue));

    const term = search.trim().toLowerCase();
    const filtered = term === '' ? options : options.filter((option) => String(option.label).toLowerCase().includes(term));
    const allFilteredSelected = filtered.length > 0 && filtered.every((option) => isSelected(option.value));

    // Chips follow the option order, so they read the same as the list.
    const chosen = options.filter((option) => isSelected(option.value));

    const open = () => {
        if (!disabled) {
            setIsOpen(true);
        }
    };

    const close = () => {
        setIsOpen(false);
        setSearch('');
    };

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const onDocMouseDown = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                close();
            }
        };
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                // Escape closes the list, not the dialog around it.
                e.stopPropagation();
                close();
            }
        };

        document.addEventListener('mousedown', onDocMouseDown, true);
        document.addEventListener('keydown', onKeyDown, true);

        return () => {
            document.removeEventListener('mousedown', onDocMouseDown, true);
            document.removeEventListener('keydown', onKeyDown, true);
        };
    }, [isOpen]);

    const toggle = (optionValue) => {
        onChange(isSelected(optionValue) ? value.filter((item) => !sameValue(item, optionValue)) : [...value, optionValue]);
    };

    const toggleAllFiltered = () => {
        const filteredValues = new Set(filtered.map((option) => String(option.value)));

        if (allFilteredSelected) {
            onChange(value.filter((item) => !filteredValues.has(String(item))));
        } else {
            onChange([...value, ...filtered.filter((option) => !isSelected(option.value)).map((option) => option.value)]);
        }
    };

    return (
        <div className="tw-relative" ref={wrapperRef}>
            {/* Clicking anywhere in the field opens the list and puts the cursor in the search box. */}
            <div
                onClick={() => {
                    open();
                    inputRef.current?.focus();
                }}
                className={classNames(
                    'tw-flex tw-min-h-[38px] tw-w-full tw-items-center tw-gap-x-2 tw-rounded-lg tw-bg-charcoal-10 tw-px-3 tw-py-2 tw-text-start',
                    invalid && 'tw-ring-1 tw-ring-red',
                    disabled ? 'tw-cursor-not-allowed tw-opacity-60' : 'tw-cursor-text',
                )}
            >
                <span className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-wrap tw-items-center tw-gap-1.5">
                    {chosen.map((option) => (
                        <SelectionChip
                            key={option.value}
                            className={FIELD_CHIP}
                            label={option.label}
                            disabled={disabled}
                            onRemove={() => toggle(option.value)}
                        />
                    ))}
                    <input
                        ref={inputRef}
                        id={id}
                        type="text"
                        role="combobox"
                        aria-expanded={isOpen}
                        aria-controls={listId}
                        aria-autocomplete="list"
                        autoComplete="off"
                        disabled={disabled}
                        value={search}
                        placeholder={(isOpen && searchPlaceholder) || placeholder || ''}
                        onFocus={open}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            open();
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                open();
                            }
                        }}
                        className="tw-min-w-[80px] tw-flex-1 tw-border-0 tw-bg-transparent tw-p-0 tw-text-sm tw-font-light tw-text-charcoal placeholder:tw-text-charcoal-50 focus:tw-outline-none focus:tw-ring-0"
                    />
                </span>
                <ChevronDownIcon className={classNames('tw-h-3 tw-w-3 tw-shrink-0 tw-text-charcoal-50 tw-transition-transform', isOpen && 'tw-rotate-180')} />
            </div>

            {isOpen && (
                <div className="tw-absolute tw-z-[9999] tw-mt-1 tw-w-full tw-rounded-[10px] tw-bg-white tw-p-1.5 tw-shadow-primary">
                    <div id={listId} role="listbox" aria-multiselectable="true" className="tw-max-h-[230px] tw-overflow-y-auto">
                        {filtered.length === 0 && <div className="tw-px-2.5 tw-py-2 tw-text-0.5sm tw-font-light tw-text-charcoal-50">{$t('No results')}</div>}

                        {filtered.map((option) => (
                            // The shared checkbox label is small and bold; list items read as light body text.
                            <div
                                key={option.value}
                                className="tw-rounded-md tw-px-2.5 tw-py-2.5 hover:tw-bg-blue-10 [&_label]:tw-w-full [&_label]:tw-cursor-pointer [&_label]:!tw-gap-x-2.5 [&_label]:!tw-text-0.5sm [&_label]:!tw-font-light"
                            >
                                <CheckboxInput label={option.label} checked={isSelected(option.value)} onChange={() => toggle(option.value)} />
                            </div>
                        ))}
                    </div>

                    <div className="tw-mt-1 tw-flex tw-items-center tw-justify-between tw-border-t tw-border-solid tw-border-x-0 tw-border-b-0 tw-border-charcoal-15 tw-px-2.5 tw-pb-1 tw-pt-2">
                        <button type="button" className={FOOTER_LINK} onClick={toggleAllFiltered} disabled={filtered.length === 0}>
                            {allFilteredSelected ? $t('Clear all') : $t('Select all')}
                        </button>
                        <button type="button" className={FOOTER_LINK} onClick={close}>
                            {$t('Done')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
